import { SocialTopicPostItem, SocialTopicPostsSort } from '@digital-twins/shared-types'

/** The value a board is ranked by, as the API sorts it (higher first); null for `newest`. */
export function socialFeedRank(post: SocialTopicPostItem, sort: SocialTopicPostsSort): number {
  const { likes, comments, shares, views } = post.engagement
  if (sort === 'views') {
    return views ?? -1
  }
  if (sort === 'engagement') {
    if (likes == null && comments == null && shares == null) {
      return -1
    }
    return (likes ?? 0) + (comments ?? 0) + (shares ?? 0)
  }
  return Date.parse(post.publishedAt ?? post.collectedAt) || 0
}

function hasStoredImage(post: SocialTopicPostItem): boolean {
  return (post.images?.length ?? 0) > 0
}

/**
 * A board's posts in display order: the API's rank order, with posts that
 * have a stored image first among posts of equal rank (stable otherwise). A
 * text tile never opens the board while an image post is loaded: the first
 * image post moves up to lead. Nothing else moves.
 */
export function orderSocialFeedPosts(
  posts: Array<SocialTopicPostItem>,
  sort: SocialTopicPostsSort,
): Array<SocialTopicPostItem> {
  // Runs of adjacent posts with the same rank (the API returns rank order):
  // image posts lead inside a run, each side keeping its order.
  const ordered: Array<SocialTopicPostItem> = []
  let start = 0
  while (start < posts.length) {
    const rank = socialFeedRank(posts[start], sort)
    let end = start + 1
    while (end < posts.length && socialFeedRank(posts[end], sort) === rank) {
      end += 1
    }
    const run = posts.slice(start, end)
    ordered.push(...run.filter(hasStoredImage), ...run.filter((post) => !hasStoredImage(post)))
    start = end
  }
  if (ordered.length > 1 && !hasStoredImage(ordered[0])) {
    const firstImage = ordered.findIndex(hasStoredImage)
    if (firstImage > 0) {
      const [lead] = ordered.splice(firstImage, 1)
      ordered.unshift(lead)
    }
  }
  return ordered
}
